/**
 * Выбор анимации и кадра по манифесту атласа — без PixiJS, чтобы проверять в unit-тестах.
 *
 * Анимации ищутся по имени `<entity>/<anim>/<dir>` (контракт `atlas.ts`). Если какой-то нет (модель A2–A5 ещё не
 * готова, у сущности одно направление `s`, у voxel-модели нет «атаки»), берётся ближайшая замена: то же действие
 * в направлении `s`, затем запасное действие ({@link FALLBACK_ANIMS}). Результат кешируется — на кадр одна выборка Map.
 */
import type { AtlasAnimation, Direction } from "@newfungame/contracts";
import { TICK_RATE } from "@newfungame/contracts";
import { DIRECTIONS } from "./iso";

/** Чем заменить отсутствующую анимацию. */
export const FALLBACK_ANIMS: Readonly<Record<string, readonly string[]>> = {
  attack: ["walk"],
  die: ["walk"],
  skill: ["shoot", "idle"],
  shoot: ["idle"],
  fire: ["idle"],
  damaged: ["intact"],
  critical: ["damaged", "intact"],
  destroyed: ["critical", "damaged", "intact"],
};

/** Сколько тиков зомби вспыхивает от попадания. */
export const HIT_FLASH_TICKS = 4;
const HIT_TINT = 0xff5a4a;

/** Цвет зомби: вспышка попадания сильнее цвета статуса, чтобы первый выстрел был заметен. */
export function hitTint(base: number, ticksSinceHit: number): number {
  return ticksSinceHit < HIT_FLASH_TICKS ? HIT_TINT : base;
}

/** Индекс кадра анимации через `ticks` тиков (дробные — между тиками) с её начала. */
export function frameIndex(anim: AtlasAnimation, ticks: number): number {
  const i = Math.floor((Math.max(0, ticks) * anim.fps) / TICK_RATE + 1e-9);
  const n = anim.frames.length;
  return anim.loop ? i % n : Math.min(i, n - 1);
}

/** Имя кадра анимации через `ticks` тиков с её начала. */
export function frameAt(anim: AtlasAnimation, ticks: number): string {
  return anim.frames[frameIndex(anim, ticks)]!;
}

/** Длительность разовой анимации, тиков (для зацикленной — один цикл). */
export function animTicks(anim: AtlasAnimation): number {
  return (anim.frames.length * TICK_RATE) / anim.fps;
}

export interface AnimResolver {
  /** Анимация сущности или замена; `null` — в атласе нет ничего подходящего. */
  get(entity: string, anim: string, dir: Direction): AtlasAnimation | null;
}

export function createAnimResolver(animations: Readonly<Record<string, AtlasAnimation>>): AnimResolver {
  // entity → anim → 8 направлений (по индексу DIRECTIONS)
  const cache = new Map<string, Map<string, (AtlasAnimation | null)[]>>();

  const lookup = (entity: string, anim: string, dir: Direction): AtlasAnimation | null => {
    const names = [anim, ...(FALLBACK_ANIMS[anim] ?? [])];
    for (const a of names) {
      const exact = animations[`${entity}/${a}/${dir}`];
      if (exact) return exact;
      const south = animations[`${entity}/${a}/s`];
      if (south) return south;
    }
    return null;
  };

  return {
    get(entity, anim, dir) {
      let byAnim = cache.get(entity);
      if (!byAnim) cache.set(entity, (byAnim = new Map()));
      let byDir = byAnim.get(anim);
      if (!byDir) {
        byDir = DIRECTIONS.map((d) => lookup(entity, anim, d));
        byAnim.set(anim, byDir);
      }
      return byDir[DIRECTIONS.indexOf(dir)] ?? null;
    },
  };
}

/** Число урона коротко: 950 → «950», 12 300 → «12.3K», 4 500 000 → «4.5M». */
export function formatDamage(v: number): string {
  if (v < 1000) return String(Math.trunc(v));
  if (v < 1_000_000) return `${trim(v / 1000)}K`;
  return `${trim(v / 1_000_000)}M`;
}

function trim(x: number): string {
  return x >= 100 ? String(Math.trunc(x)) : (Math.trunc(x * 10) / 10).toString();
}
